// Row types and the date helper shared by the Security section cards.

export interface SessionRow {
  id: string;
  deviceName: string | null;
  userAgent: string | null;
  ip: string | null;
  lastActivityAt: string | null;
  createdAt: string;
  current: boolean;
}

export interface AuditRow {
  id: string;
  eventType: string;
  ip: string | null;
  createdAt: string;
}

/**
 * The Intl locale for an interface language: the two bundled languages get their
 * region (et -> et-EE, en -> en-GB), and any other code (a runtime language such
 * as "pl") is passed as is, so its dates follow that language.
 */
export function intlLocale(locale: string): string {
  if (locale === "et") return "et-EE";
  if (locale === "en") return "en-GB";
  return locale;
}

export function fmt(iso: string | null, locale: string): string {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleString(intlLocale(locale), { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return "-";
  }
}

/**
 * The AI apps' connection address (the site MCP server), worked out from the host the
 * account site runs on, the same way the other app addresses are (`lib/auth.ts`):
 * account.husly.app -> https://api.husly.app/mcp; local development -> the API on :3311.
 */
export function mcpAddress(loc: { hostname: string; protocol: string }): string {
  const { hostname, protocol } = loc;
  if (hostname === "localhost" || hostname.endsWith(".localhost")) return "http://localhost:3311/mcp";
  const base = hostname.split(".").slice(1).join(".") || hostname;
  return `${protocol}//api.${base}/mcp`;
}

/** The AI apps that connect with that address, in tab order, with their brand names. */
export const AI_APPS = ["claude", "chatgpt", "codex"] as const;
export type AiApp = (typeof AI_APPS)[number];
export const AI_APP_NAMES: Record<AiApp, string> = { claude: "Claude", chatgpt: "ChatGPT", codex: "Codex" };

/** The terminal line that adds Husly to Codex; Codex then opens the Husly login by itself. */
export function codexCommand(address: string): string {
  return `codex mcp add husly --url ${address}`;
}

/**
 * The developers page section that explains the connection, in the reader's language:
 * Claude's own section, or the one ChatGPT and Codex share below it.
 */
export function mcpGuidePath(locale: string, app: AiApp = "claude"): string {
  const anchor = app === "claude" ? "claude" : "chatgpt-codex";
  return locale === "et" ? `/et/arendajatele#${anchor}` : `/en/developers#${anchor}`;
}
